import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useUserContext } from "./user-context";
import { getAllApp, getAllProfileByAppID } from "./client";

export type ProfileData = { [id: number]: { name: string } };
export type AppData = { [id: number]: { name: string; profiles: ProfileData } };

export interface LogContextType {
  apps: AppData;
  getAppName: (appId: number) => string | undefined;
  getProfiles: (appId: number) => ProfileData | undefined;
  getProfileName: (appId: number, profileId: number) => string | undefined;
}

const LogContext = createContext<LogContextType | undefined>(undefined);

export const useLogContext = () => {
  const context = useContext(LogContext);
  if (!context) {
    throw new Error("useLogContext must be used within a LogContextProvider");
  }
  return context;
};

export const LogContextProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const { user, isUserLoggedIn } = useUserContext();
  const isLoggedIn = isUserLoggedIn();
  const [apps, setApps] = useState<AppData>({});

  const fetchApps = useCallback(
    async (module: string) => {
      if (!(isLoggedIn && user)) return;

      const response = await getAllApp(module);
      const result: AppData = {};

      for (const [id, value] of Object.entries(response)) {
        result[Number(id)] = { name: value, profiles: {} };
        const response = await getAllProfileByAppID(Number(id), module);
        const profileResult: ProfileData = {};
        for (const [id, value] of Object.entries(response)) {
          profileResult[Number(id)] = { name: value };
        }

        result[Number(id)].profiles = profileResult;
      }

      setApps(result);
    },
    [isLoggedIn, user]
  );

  const getAppName = (appId: number): string | undefined => {
    return apps[appId]?.name;
  };

  const getProfiles = (appId: number): ProfileData | undefined => {
    return apps[appId]?.profiles;
  };

  const getProfileName = (
    appId: number,
    profileId: number
  ): string | undefined => {
    return apps[appId]?.profiles[profileId]?.name;
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchApps("log");
      setIsInitialized(true);
    };

    if (!isInitialized) {
      fetchData();
    }
  }, []);

  if (!isInitialized) return null;

  const contextValue: LogContextType = {
    apps,
    getAppName,
    getProfiles,
    getProfileName,
  };

  return (
    <LogContext.Provider value={contextValue}>{children}</LogContext.Provider>
  );
};
